import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionNum, PrimaryCTA } from "@/components/ui-bits";
import { getPracticeArea, VISIBLE_PRACTICE_AREAS } from "@/data/practiceAreas";
import { getPracticeAreaDetail } from "@/data/practiceAreaDetails";
import { getIndustriesForPracticeArea } from "@/data/industries";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function PracticeArea() {
  const { slug } = useParams();
  const area = getPracticeArea(slug);
  if (!area) return <Navigate to="/services" replace />;

  const detail = getPracticeAreaDetail(slug);
  const others = VISIBLE_PRACTICE_AREAS.filter((p) => p.slug !== area.slug);
  const relatedIndustries = getIndustriesForPracticeArea(area.slug);

  return (
    <Layout>
      <SEO
        title={area.name}
        description={`${area.name} — ${area.kicker} ${area.lead.slice(0, 120)}`}
        path={`/services/${area.slug}`}
      />
      <section className="border-b border-hairline" data-testid={`practice-hero-${area.slug}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-soft hover:text-brass mb-10" data-testid="back-to-services">
            <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Practice Areas
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-2">
              <SectionNum className="text-2xl">{area.number}</SectionNum>
            </div>
            <div className="lg:col-span-10">
              <Eyebrow>Practice Area</Eyebrow>
              <h1 className="display-1 text-ink mt-4">{area.name}</h1>
              <p className="mt-8 font-serif italic text-2xl text-ink-soft leading-snug max-w-3xl">{area.kicker}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline" data-testid="practice-body">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Overview</Eyebrow>
            <h2 className="font-serif text-3xl text-ink mt-4 leading-tight">Scope of the Practice</h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-ink text-[1.125rem] leading-relaxed font-serif italic border-l border-brass pl-6">
              {area.lead}
            </p>
          </div>
        </div>
      </section>

      {(detail?.serves?.length > 0 || detail?.typicalMatters?.length > 0) && (
        <section className="border-b border-hairline bg-ivory-alt" data-testid="practice-callouts">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
            {detail.serves?.length > 0 && (
              <div data-testid="practice-serves">
                <Eyebrow>Who This Serves</Eyebrow>
                <ul className="mt-6 border-t border-hairline">
                  {detail.serves.map((s, i) => (
                    <li key={i} className="border-b border-hairline py-3 text-ink text-[1.0625rem] leading-relaxed">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {detail.typicalMatters?.length > 0 && (
              <div data-testid="practice-typical-matters">
                <Eyebrow>Typical Matters</Eyebrow>
                <ul className="mt-6 border-t border-hairline">
                  {detail.typicalMatters.map((m, i) => (
                    <li key={i} className="border-b border-hairline py-3 text-ink text-[1.0625rem] leading-relaxed">
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="border-b border-hairline" data-testid="practice-sections">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          {area.sections.map((s, idx) => (
            <div
              key={s.h}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12 py-10 border-t border-hairline first:border-t-0"
              data-testid={`practice-section-${idx}`}
            >
              <div className="lg:col-span-4">
                <span className="section-num">{String(idx + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-2xl text-ink mt-3 leading-tight">{s.h}</h3>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                <p className="text-ink-soft text-[1.0625rem] leading-relaxed">{s.p}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {relatedIndustries.length > 0 && (
        <section className="border-b border-hairline bg-ivory-alt" data-testid="practice-related-industries">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Industry Context</Eyebrow>
              <h2 className="font-serif text-3xl text-ink mt-4 leading-tight">
                Industries we advise in this area
              </h2>
              <p className="mt-5 text-ink-soft text-[0.95rem] leading-relaxed">
                Sectors where {area.name.toLowerCase()} work sits at the center of the client&apos;s regulatory profile.
              </p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <ul className="border-t border-hairline">
                {relatedIndustries.map((ind) => (
                  <li key={ind.slug} className="border-b border-hairline">
                    <Link
                      to="/industries"
                      className="group grid grid-cols-12 gap-6 py-5 items-baseline hover:bg-ivory transition-colors -mx-3 px-3"
                      data-testid={`practice-related-industry-${ind.slug}`}
                    >
                      <SectionNum className="col-span-2 sm:col-span-1">{ind.number}</SectionNum>
                      <div className="col-span-9 sm:col-span-10">
                        <h3 className="font-serif text-xl sm:text-2xl text-ink group-hover:text-brass transition-colors">
                          {ind.name}
                        </h3>
                        <p className="mt-1 text-ink-soft text-[0.95rem] leading-snug">{ind.kicker}</p>
                      </div>
                      <div className="hidden sm:flex col-span-1 justify-end">
                        <ArrowUpRight className="w-4 h-4 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="bg-ink text-ivory" data-testid="practice-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-5">For Inquiries</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
            Contact the firm to discuss a matter in {area.name.toLowerCase()}.
          </h2>
          <div className="mt-10 flex justify-center">
            <PrimaryCTA to="/contact" testId="practice-cta-button">Contact the Firm</PrimaryCTA>
          </div>
        </div>
      </section>

      <section data-testid="practice-other-areas">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <Eyebrow>Other Practice Areas</Eyebrow>
          <ul className="border-t border-hairline mt-8">
            {others.map((o) => (
              <li key={o.slug} className="border-b border-hairline">
                <Link
                  to={`/services/${o.slug}`}
                  className="group grid grid-cols-12 gap-6 py-6 items-baseline hover:bg-ivory-alt transition-colors -mx-3 px-3"
                  data-testid={`practice-other-${o.slug}`}
                >
                  <SectionNum className="col-span-2 sm:col-span-1">{o.number}</SectionNum>
                  <div className="col-span-10 sm:col-span-8">
                    <h3 className="font-serif text-xl sm:text-2xl text-ink group-hover:text-brass transition-colors">{o.name}</h3>
                  </div>
                  <div className="hidden sm:flex col-span-3 justify-end">
                    <ArrowUpRight className="w-4 h-4 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </Layout>
  );
}
